<template>
  <v-form
    ref="form"
    v-model="valid"
    :lazy-validation="lazy"
    onsubmit="return false;"
    class="transporter-edit-form"
  >
    <div>
      <v-progress-linear
        indeterminate
        color="yellow darken-2"
        v-show="x.loading"
      ></v-progress-linear>
      <v-card flat color="white" class="ops-entry-form-card transporter-edit-card">
        <v-card-text class="content-border ops-entry-form-body transporter-edit-body">
          <div
            class="ops-entry-form-progress"
            aria-label="Transporter detail form sections"
          >
            <div class="ops-entry-form-progress__heading">Sections</div>
            <button
              type="button"
              data-ops-section="transporter-edit-company-details"
              @click="scrollToOpsFormSection('transporter-edit-company-details')"
              :class="[
                'ops-entry-form-progress__item',
                { 'is-active': activeTransporterFormSection === 'transporter-edit-company-details' },
                { 'is-complete': transporterFormSectionCompletion.company },
                { 'is-optional': transporterFormSectionProgress.company.optional },
              ]"
            >
              <span>01</span>
              <strong>Company Details</strong>
              <em class="ops-entry-form-progress__count">
                {{ transporterFormSectionProgress.company.completedCount }}/{{
                  transporterFormSectionProgress.company.requiredCount
                }}
              </em>
            </button>
            <button
              type="button"
              data-ops-section="transporter-edit-documentation"
              @click="scrollToOpsFormSection('transporter-edit-documentation')"
              :class="[
                'ops-entry-form-progress__item',
                { 'is-active': activeTransporterFormSection === 'transporter-edit-documentation' },
                { 'is-complete': transporterFormSectionCompletion.documentation },
                { 'is-optional': transporterFormSectionProgress.documentation.optional },
              ]"
            >
              <span>02</span>
              <strong>Documentation</strong>
              <em class="ops-entry-form-progress__count">
                {{
                  transporterFormSectionProgress.documentation.completedCount
                }}/{{ transporterFormSectionProgress.documentation.requiredCount }}
              </em>
            </button>
            <button
              type="button"
              data-ops-section="transporter-edit-contact-details"
              @click="scrollToOpsFormSection('transporter-edit-contact-details')"
              :class="[
                'ops-entry-form-progress__item',
                { 'is-active': activeTransporterFormSection === 'transporter-edit-contact-details' },
                { 'is-complete': transporterFormSectionCompletion.contact },
                { 'is-optional': transporterFormSectionProgress.contact.optional },
              ]"
            >
              <span>03</span>
              <strong>Contact Details</strong>
              <em class="ops-entry-form-progress__count">
                {{ transporterFormSectionProgress.contact.completedCount }}/{{
                  transporterFormSectionProgress.contact.requiredCount
                }}
              </em>
            </button>
            <div class="ops-entry-form-progress__summary">
              <span>Completion</span>
              <i
                role="progressbar"
                aria-label="Completion"
                :aria-valuenow="transporterFormCompletion"
                aria-valuemin="0"
                aria-valuemax="100"
                :style="{ '--ops-entry-completion': transporterFormCompletion + '%' }"
              ></i>
              <strong>
                {{ transporterCompletedRequiredCount }}/{{
                  transporterTotalRequiredCount
                }}
                required fields
              </strong>
            </div>
          </div>
          <div class="ops-entry-form-main">
          <div id="transporter-edit-company-details" class="heading primary-clr">
            <span>Company Details</span>
            <button
              v-if="readonly"
              v-permissions="'edit-transporter'"
              type="button"
              class="ops-entry-form-edit-btn"
              title="Click to enable editing of this transporter"
              aria-label="Edit transporter"
              @click="checkEdit()"
            >
              <v-icon small>create</v-icon>
              <span>Edit transporter</span>
            </button>
          </div>
          <v-layout
            row
            wrap
            class="transporter-section-grid transporter-section-grid--company"
          >
            <v-flex xs12 md4 class="transporter-field-cell transporter-radio-cell">
              <div class="subheading muted caption">Registering As</div>
              <div class="heading transporter-radio-heading">
                <v-radio-group
                  class="transporter-radio-group mt-0 pt-0"
                  v-model="type"
                  row
                  single-line
                  :disabled="readonly"
                  :mandatory="true"
                  :rules="[rules.required]"
                >
                  <v-radio
                    label="Transporter"
                    value="Transporter"
                    color="black"
                    class="transporter-radio-option"
                  ></v-radio>
                  <v-radio
                    label="Broker"
                    value="Broker"
                    color="black"
                    class="transporter-radio-option"
                  ></v-radio>
                </v-radio-group>
              </div>
            </v-flex>
            <v-flex xs12 md4 class="transporter-field-cell transporter-country-cell">
              <div class="subheading muted caption">Country*</div>
              <div class="heading">
                <v-text-field
                  readonly
                  label="Country*"
                  class="pt-0 currencyTitle"
                  v-model="countrySortName"
                  :value="countrySortName"
                  single-line
                />
              </div>
            </v-flex>
            <v-flex xs12 md4 class="transporter-field-cell transporter-type-cell">
              <div class="subheading muted caption">
                Choose Transporter Type*
              </div>
              <div class="heading">
                <v-select
                  label="Choose Transporter Type*"
                  class="pt-0 currencyTitle transporter-type-select"
                  :items="UserTypes"
                  v-model="UserType"
                  single-line
                  disabled
                  :rules="[rules.required]"
                ></v-select>
              </div>
            </v-flex>
            <v-flex xs12 md4 class="transporter-field-cell">
              <div class="subheading muted caption">Name of Company*</div>
              <div class="heading">
                <v-text-field
                  label="Name of Company *"
                  v-model="companyName"
                  single-line
                  :disabled="readonly"
                  class="pt-0 currencyTitle"
                  :rules="[rules.noWhiteSpace, rules.required]"
                />
              </div>
            </v-flex>
            <v-flex xs12 md4 class="transporter-field-cell">
              <div class="subheading muted caption">Username*</div>
              <div class="heading">
                <v-text-field
                  label="Username *"
                  v-model="username"
                  single-line
                  :disabled="readonly"
                  class="pt-0 currencyTitle"
                  :rules="[rules.noWhiteSpace, rules.required]"
                  required
                  maxlength="90"
                />
              </div>
            </v-flex>
            <v-flex xs12 md4 class="transporter-field-cell">
              <div class="subheading muted caption">Email Id*</div>
              <div class="heading">
                <v-text-field
                  label="Email Id *"
                  :rules="[rules.required]"
                  v-model="emailId"
                  single-line
                  :disabled="readonly"
                  class="pt-0 currencyTitle"
                  required
                />
              </div>
            </v-flex>
            <v-flex xs12 md4 class="transporter-field-cell">
              <div class="subheading muted caption">Password</div>
              <div class="heading">
                <v-text-field
                  autocomplete="off"
                  label="Password"
                  v-model="password"
                  type="password"
                  :disabled="readonly"
                  class="pt-0 currencyTitle"
                  single-line
                />
              </div>
            </v-flex>
            <v-flex
              xs12
              md4
              class="transporter-field-cell transporter-secondary-email-cell"
            >
              <div class="subheading muted caption">Secondary Email Id</div>
              <div class="heading">
                <v-combobox
                  v-model="model"
                  :disabled="readonly"
                  append-icon
                  label="Secondary Email Id"
                  multiple
                  single-line
                  chips
                  deletable-chips
                  class="tag-input pt-0 currencyTitle"
                  v-on:keyup.17="checkValue"
                  type="email"
                  small-chips
                  hide-details
                ></v-combobox>
              </div>
            </v-flex>
            <v-flex
              xs12
              md4
              class="transporter-field-cell transporter-upload-cell transporter-upload-cell--profile"
            >
              <div class="subheading muted caption">Profile Image</div>
              <div class="transporter-upload-row">
                <div
                  v-if="!readonly"
                  class="upload-btn-wrapper transporter-upload-action"
                >
                  <button class="btn">Upload Profile Image</button>
                  <input
                    type="file"
                    name="myfile"
                    ref="profilePicUpload"
                    accept="image/png, image/jpeg, .png, .jpg, .jpeg"
                    @change="profilePicChange"
                  />
                </div>
                <a
                  v-if="profilePicURL"
                  :href="profilePicURL"
                  title="Open profile image in a new tab"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="transporter-profile-preview"
                >
                  <img
                    :src="profilePicURL"
                    class="transporter-profile-preview__thumb"
                    alt="Profile image"
                  />
                  <span class="transporter-profile-preview__copy">
                    <span class="transporter-profile-preview__title">Profile image</span>
                    <span class="transporter-profile-preview__hint">View full size</span>
                  </span>
                  <v-icon small class="transporter-profile-preview__icon">open_in_new</v-icon>
                </a>
              </div>
            </v-flex>
            <v-flex
              xs12
              sm6
              md4
              class="transporter-field-cell"
              v-if="UserType == 1 && countrySortName == 'UAE'"
            >
              <div class="subheading muted caption">All Banks Name</div>
              <div class="heading">
                <v-autocomplete
                  style="background-color: white !important"
                  attach
                  :items="bankInfo"
                  item-text="bankName"
                  item-value="bankName"
                  v-model="bankName"
                  :value="bankName"
                  label="All Banks Name"
                  class="pt-0 currencyTitle"
                  :disabled="readonly"
                  single-line
                ></v-autocomplete>
              </div>
            </v-flex>
            <v-flex
              xs12
              sm6
              md4
              class="transporter-field-cell"
              v-if="UserType == 1 && countrySortName == 'UAE'"
            >
              <div class="subheading muted caption">Account Holder Name</div>
              <div class="heading">
                <v-text-field
                  class="pt-0 currencyTitle"
                  v-model="bankHolderName"
                  :xvalue="bankHolderName"
                  label="Account Holder Name"
                  :rules="[rules.onlyEnglish]"
                  :disabled="readonly"
                  single-line
                ></v-text-field>
              </div>
            </v-flex>
          </v-layout>

          <div id="transporter-edit-documentation" class="heading primary-clr">
            Documentation
          </div>
          <v-layout
            row
            wrap
            class="transporter-section-grid transporter-section-grid--documents"
          >
            <v-flex
              xs12
              md4
              class="transporter-field-cell transporter-upload-cell transporter-doc-field--license-file"
            >
              <div class="subheading muted caption">
                {{
                  countrySortName != "PAK"
                    ? "Trade License/CR Copy"
                    : "NTN Certificate"
                }}
              </div>
              <div class="transporter-upload-row">
                <div
                  v-if="!readonly"
                  class="upload-btn-wrapper transporter-upload-action"
                >
                  <button class="btn">
                    {{
                      countrySortName != "PAK"
                        ? "Upload Trade License/CR Copy"
                        : "Upload NTN Certificate"
                    }}
                  </button>
                  <input
                    type="file"
                    name="myfile"
                    ref="fileUpload"
                    accept=".pdf, .jpeg, .jpg, .png"
                    @change="tradeLicenseChange"
                  />
                </div>
                <a
                  v-if="tradeLicense"
                  target="_blank"
                  :href="tradeLicense"
                  class="transporter-upload-link outline-border"
                >
                  {{
                    countrySortName != "PAK"
                      ? "View Trade License/CR Copy"
                      : "View NTN Certificate"
                  }}
                </a>
              </div>
            </v-flex>
            <v-flex
              xs12
              md4
              class="transporter-field-cell transporter-upload-cell transporter-doc-field--tax-file"
            >
              <div class="subheading muted caption">
                {{
                  countrySortName != "PAK"
                    ? "VAT Certificate"
                    : "STRN Certificate"
                }}
              </div>
              <div class="transporter-upload-row">
                <div
                  v-if="!readonly"
                  class="upload-btn-wrapper transporter-upload-action"
                >
                  <button class="btn">
                    {{
                      countrySortName != "PAK"
                        ? "Upload VAT Certificate"
                        : "Upload STRN Certificate"
                    }}
                  </button>
                  <input
                    type="file"
                    ref="fileUpload"
                    accept=".pdf, .jpeg, .jpg, .png"
                    name="myfile"
                    @change="vatCertificateChange"
                  />
                </div>
                <a
                  v-if="vatCertificate"
                  target="_blank"
                  :href="vatCertificate"
                  class="transporter-upload-link outline-border"
                >
                  {{
                    countrySortName != "PAK"
                      ? "View VAT Certificate"
                      : "View STRN Certificate"
                  }}
                </a>
              </div>
            </v-flex>
            <v-flex
              xs12
              md4
              class="transporter-field-cell transporter-upload-cell transporter-doc-field--passport-front-file"
              v-if="countrySortName != 'PAK'"
            >
              <div class="subheading muted caption">Passport Front Image</div>
              <div class="transporter-upload-row">
                <div
                  v-if="!readonly"
                  class="upload-btn-wrapper transporter-upload-action"
                >
                  <button class="btn">Upload Passport Front Image</button>
                  <input
                    ref="fileUpload"
                    accept=".pdf, .jpeg, .jpg, .png"
                    type="file"
                    name="myfile"
                    @change="passportFrontChange"
                  />
                </div>
                <a
                  v-if="passportFront"
                  target="_blank"
                  :href="passportFront"
                  class="transporter-upload-link outline-border"
                >
                  View Passport
                </a>
              </div>
            </v-flex>
            <v-flex
              xs12
              md4
              class="transporter-field-cell transporter-upload-cell transporter-doc-field--passport-back-file"
              v-if="countrySortName != 'PAK'"
            >
              <div class="subheading muted caption">Passport Back Image</div>
              <div class="transporter-upload-row">
                <div
                  v-if="!readonly"
                  class="upload-btn-wrapper transporter-upload-action"
                >
                  <button class="btn">Upload Passport Back Image</button>
                  <input
                    ref="fileUpload"
                    accept=".pdf, .jpeg, .jpg, .png"
                    type="file"
                    name="myfile"
                    @change="passportBackChange"
                  />
                </div>
                <a
                  v-if="passportBack"
                  target="_blank"
                  :href="passportBack"
                  class="transporter-upload-link outline-border"
                >
                  View Passport
                </a>
              </div>
            </v-flex>
            <v-flex
              xs12
              md4
              class="transporter-field-cell transporter-upload-cell transporter-doc-field--cnic-front-file"
              v-if="countrySortName == 'PAK'"
            >
              <div class="subheading muted caption">CNIC Front Image</div>
              <div class="transporter-upload-row">
                <div
                  v-if="!readonly"
                  class="upload-btn-wrapper transporter-upload-action"
                >
                  <button class="btn">Upload CNIC Front Image</button>
                  <input
                    ref="fileUpload"
                    accept=".pdf, .jpeg, .jpg, .png"
                    type="file"
                    name="myfile"
                    @change="CnicFrontChange"
                  />
                </div>
                <a
                  v-if="CNICFront"
                  target="_blank"
                  :href="CNICFront"
                  class="transporter-upload-link outline-border"
                >
                  View CNIC
                </a>
              </div>
            </v-flex>
            <v-flex
              xs12
              md4
              class="transporter-field-cell transporter-upload-cell transporter-doc-field--cnic-back-file"
              v-if="countrySortName == 'PAK'"
            >
              <div class="subheading muted caption">CNIC Back Image</div>
              <div class="transporter-upload-row">
                <div
                  v-if="!readonly"
                  class="upload-btn-wrapper transporter-upload-action"
                >
                  <button class="btn">Upload CNIC Back Image</button>
                  <input
                    ref="fileUpload"
                    accept=".pdf, .jpeg, .jpg, .png"
                    type="file"
                    name="myfile"
                    @change="CnicBackChange"
                  />
                </div>
                <a
                  v-if="CNICBack"
                  target="_blank"
                  :href="CNICBack"
                  class="transporter-upload-link outline-border"
                >
                  View CNIC
                </a>
              </div>
            </v-flex>
            <v-flex
              xs12
              md4
              class="transporter-field-cell transporter-doc-field--license-number"
              v-if="countrySortName != 'PAK'"
            >
              <div class="subheading muted caption">Trade Licence Number *</div>
              <div class="heading">
                <v-text-field
                  label="Trade Licence Number *"
                  v-model="tradeLicenseNumber"
                  required
                  :disabled="readonly"
                  class="pt-0 currencyTitle"
                  :rules="[rules.noWhiteSpace, rules.required]"
                  single-line
                />
              </div>
            </v-flex>
            <v-flex
              xs12
              md4
              class="transporter-field-cell transporter-doc-field--tax-number"
              v-if="countrySortName != 'PAK'"
            >
              <div class="subheading muted caption">VAT Number</div>
              <div class="heading">
                <v-text-field
                  label="VAT Number"
                  v-model="vatNumber"
                  :disabled="readonly"
                  class="pt-0 currencyTitle"
                  single-line
                />
              </div>
            </v-flex>
            <v-flex
              xs12
              md4
              class="transporter-field-cell transporter-doc-field--passport-number"
              v-if="countrySortName != 'PAK'"
            >
              <div class="subheading muted caption">Passport number*</div>
              <div class="heading">
                <v-text-field
                  autocomplete="off"
                  label="Passport Number *"
                  v-model="passport"
                  :disabled="readonly"
                  required
                  :rules="[rules.required]"
                  class="pt-0 currencyTitle"
                  single-line
                />
              </div>
            </v-flex>
            <v-flex
              xs12
              md4
              class="transporter-field-cell transporter-doc-field--ntn-number"
              v-if="countrySortName == 'PAK'"
            >
              <div class="subheading muted caption">NTN Number*</div>
              <div class="heading">
                <v-text-field
                  label="NTN Number *"
                  v-model="NTNNumber"
                  single-line
                  class="pt-0 currencyTitle"
                  :rules="[rules.noWhiteSpace, rules.required]"
                  required
                  :disabled="readonly"
                />
              </div>
            </v-flex>
            <v-flex
              xs12
              md4
              class="transporter-field-cell transporter-doc-field--strn-number"
              v-if="countrySortName == 'PAK'"
            >
              <div class="subheading muted caption">STRN Number</div>
              <div class="heading">
                <v-text-field
                  label="STRN Number"
                  :disabled="readonly"
                  v-model="STRNNumber"
                  single-line
                  class="pt-0 currencyTitle"
                  required
                  counter
                  maxlength="13"
                />
              </div>
            </v-flex>
          </v-layout>

          <div id="transporter-edit-contact-details" class="heading primary-clr">
            Contact Details
          </div>
          <v-layout
            row
            wrap
            class="transporter-section-grid transporter-section-grid--contact"
          >
            <v-flex
              xs12
              sm6
              md3
              class="transporter-field-cell transporter-phone-code-cell"
            >
              <div class="subheading muted caption">Country Code*</div>
              <div class="heading">
                <v-autocomplete
                  v-model="countryCode"
                  id="countryCode"
                  item-text="dialing_code"
                  item-value="dialing_code"
                  label="Country Code *"
                  :items="countryCodes"
                  persistent-hint
                  return-object
                  class="pt-0 currencyTitle"
                  single-line
                  :disabled="readonly"
                  :rules="[rules.required]"
                ></v-autocomplete>
              </div>
            </v-flex>
            <v-flex
              xs12
              sm6
              md3
              class="transporter-field-cell transporter-phone-cell"
            >
              <div class="subheading muted caption">Phone Number*</div>
              <div class="heading">
                <v-text-field
                  single-line
                  label="Phone Number*"
                  class="pt-0 currencyTitle"
                  :disabled="readonly"
                  :rules="[rules.phoneNumber, rules.required]"
                  v-model="phoneNo"
                ></v-text-field>
              </div>
            </v-flex>
            <v-flex
              xs12
              md4
              class="transporter-field-cell"
              v-if="countrySortName == 'PAK'"
            >
              <div class="subheading muted caption">Contact Person Name*</div>
              <div class="heading">
                <v-text-field
                  label="Contact Person Name  *"
                  v-model="ContactPersonName"
                  single-line
                  :disabled="readonly"
                  class="pt-0 currencyTitle"
                  :rules="[rules.noWhiteSpace, rules.required]"
                  required
                />
              </div>
            </v-flex>
            <v-flex xs12 md4 class="transporter-field-cell">
              <div class="subheading muted caption">Comments *</div>
              <div class="heading">
                <v-text-field
                  label="Comments *"
                  single-line
                  class="pt-0 currencyTitle"
                  v-model="comment"
                  :disabled="readonly"
                  :rules="[rules.required]"
                />
              </div>
            </v-flex>
          </v-layout>

          <div
            class="my-3"
            v-if="haserror"
            style="width: 50%; margin: auto; text-align: center"
          >
            <v-alert v-model="haserror" class="py-2" outline color="red">{{
              x.error
            }}</v-alert>
          </div>
          </div>
          <v-layout class="ops-entry-form-actions">
            <v-spacer />
            <v-btn
              size="large"
              color="grey darken-3"
              flat
              class="ops-entry-form-cancel"
              style="color: white"
              v-if="!readonly"
              @click.native="
                readonly = !readonly;
                getTransporter();
              "
              >Cancel</v-btn
            >
            <v-btn
              size="large"
              color="orange darken-1"
              class="ops-entry-form-submit"
              style="color: white"
              v-if="!readonly"
              @click.native="checkUpdate()"
              >Update Profile</v-btn
            >
          </v-layout>
        </v-card-text>
      </v-card>
      <success-dialog
        content="Edited successfully"
        :show="x.registerSuccess"
        :onclose="closeRegistration"
      />
    </div>
  </v-form>
</template>

<script>
import SuccessDialog from "@/components/Common/SuccessDialog";
import { EventBus } from "../../event-bus.js";
import ErrorBox from "@/components/Common/ErrorBox";
import moment from "moment";
import { StorageKeys, UserTypes, countries } from "../../constants/constants";
import { getUserById, transporter } from "../../constants/api-urls";
import { scrollToOpsFormSection } from "@/utils/opsFormScroll";
import {
  buildTransporterProgressSections,
  getOpsFormCompletedCount,
  getOpsFormCompletionPercent,
  getOpsFormRequiredCount,
  mapOpsFormSectionsByKey,
} from "@/utils/opsFormProgress";
export default {
  created() {
    this.fetchBank();
    if (localStorage.getItem("workingCountries")) {
      let workingCountries = JSON.parse(
        localStorage.getItem("workingCountries")
      );
      workingCountries.forEach((key, index) => {
        if (key.isSelected) {
          this.countrySortName = key.value;
        }
      });
    }

    EventBus.$on("countryChanged", (val) => {
      if (this.$route.name == "transporter-detail") {
        this.$router.push(`/transporter`);
      }
      this.countrySortName = val.value;
    });
    this.UserTypes = UserTypes;
    this.countryCodes = countries;
    this.getTransporter();
    this.getUserById();
  },
  components: {
    SuccessDialog,
    ErrorBox,
  },
  props: {
    profile: Object,
  },
  data() {
    return {
      activeTransporterFormSection: "transporter-edit-company-details",
      transporterType: [
        { value: "Transporter", text: "Transporter" },
        { value: "Broker", text: "Broker" },
      ],
      type: "",
      STRNNumber: "",
      tradeLicenseNumber: "",
      vatNumber: "",
      passport: "",
      CNICFront: "",
      CNICBack: "",
      operationName: "",
      ContactPersonName: "",
      NTNNumber: "",
      countrySortName: "",
      multipleCountry: [],
      phoneNo: "",
      countryCodes: [],
      countryCode: "",
      valid: true,
      lazy: false,
      readonly: true,
      model: [],
      companyName: "",
      username: "",
      profilePicURL: "",
      secondaryEmail: [],
      UserTypes: [],
      UserType: "",
      emailId: "",
      comment: "",
      passportBack: "",
      passportFront: "",
      tradeLicense: "",
      vatCertificate: "",
      password: "",
      bankHolderName: "",
      bankName: "",
      bankInfo: [],

      processing: false,
      x: {
        loading: false,
        error: null,
        message: "",
        registerSuccess: false,
      },
      rules: {
        noWhiteSpace: (v) =>
          (v != null && v.trim().length > 0) || "This field is required.",
        required: (value) => !!value || "This field is required.",
        min: (v) =>
          v.length >= 4 || "Password length must be of minimum 4 characters",
        phoneNumber: (v) =>
          /^[0-9]{5,15}$/.test(v) || "Please Enter Valid Phone No.",
        NTNNumber: (v) =>
          /^([a-zA-Z0-9_-]{13})$/.test(v) || "Please Enter Valid Number",
        emailRules: (v) =>
          /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/.test(
            v
          ) || "E-mail must be valid",
        userName: (v) =>
          /^[a-z0-9_-]{6,15}$/.test(v) ||
          "Username must be of minimum 6 characters.",
      },
    };
  },
  computed: {
    haserror() {
      return this.x.error !== null;
    },
    transporterFormSections() {
      return buildTransporterProgressSections(this, {
        sectionIds: {
          company: "transporter-edit-company-details",
          documentation: "transporter-edit-documentation",
          contact: "transporter-edit-contact-details",
        },
      });
    },
    transporterFormSectionProgress() {
      return mapOpsFormSectionsByKey(this.transporterFormSections);
    },
    transporterFormSectionCompletion() {
      return Object.keys(this.transporterFormSectionProgress).reduce(
        (completionMap, sectionKey) => {
          completionMap[sectionKey] =
            this.transporterFormSectionProgress[sectionKey].complete;
          return completionMap;
        },
        {}
      );
    },
    transporterCompletedRequiredCount() {
      return getOpsFormCompletedCount(this.transporterFormSections);
    },
    transporterTotalRequiredCount() {
      return getOpsFormRequiredCount(this.transporterFormSections);
    },
    transporterFormCompletion() {
      return getOpsFormCompletionPercent(this.transporterFormSections);
    },
  },
  watch: {
    model(val) {
      if (val.length > 3) {
        this.$nextTick(() => this.model.pop());
      }
    },
  },
  methods: {
    scrollToOpsFormSection(sectionId) {
      this.activeTransporterFormSection = sectionId;
      scrollToOpsFormSection(this, sectionId);
    },
    async checkEdit() {
      this.x.error = null;
      this.haserror = false;
      this.operationName = "edit-transporter";
      let y = await this.checkOpertaionPermission(this.operationName);
      if (y) {
        this.readonly = !this.readonly;
      } else {
        return;
      }
    },
    fetchBank() {
      delete this.axios.defaults.headers.common["token"];
      let token = localStorage.getItem(StorageKeys.SessionKey);
      let config = {
        headers: {
          authorization: `bearer ${token}`,
        },
      };
      let body = {};

      let { apiUrl } = this.constants;
      this.axios
        .post(`${apiUrl}/dmsAdmin/bankListForDriver`, body, config)
        .then(
          (response) => {
            let data = response.data.data;
            console.log(data);
            this.bankInfo = data;
            // data.forEach((element) => {
            //   this.bankInfo.push({
            //     text: element.bankName,
            //     value: element._id,
            //   });
            // });
            // console.log(this.banking.info);

            // this.bankName = response.data.data[2].bankName;
          },
          (error) => {
            this.error = "Something went wrong. Please try again later!";
            this.loading = false;
          }
        );
    },
    async checkUpdate() {
      this.x.error = null;
      this.haserror = false;
      this.operationName = "edit-transporter";
      let y = await this.checkOpertaionPermission(this.operationName);
      if (y) {
        this.editTransporter();
      } else {
        this.readonly = !this.readonly;
        return;
      }
    },
    getUserById() {
      this.userId = localStorage.getItem("userId");
      this.loading = true;
      let url = getUserById;
      delete this.axios.defaults.headers.common["token"];
      let body = {
        userId: localStorage.getItem("userId"),
      };
      let token = localStorage.getItem(StorageKeys.SessionKey);
      let config = {
        headers: {
          authorization: `bearer ${token}`,
        },
      };
      this.axios.post(this.constants.apiUrl + url, body, config).then(
        (response) => {
          response.data.data.countrySortName.forEach((e) => {
            this.multipleCountry.push(e);
          });
        },
        (error) => {
          this.x.error = "Failed to fetch country";
        }
      );
    },
    tradeLicenseChange(e) {
      var files = e.target.files || e.dataTransfer.files;
      if (!files.length) return;
      this.createImage(files[0], 1);
    },
    vatCertificateChange(e) {
      var files = e.target.files || e.dataTransfer.files;
      if (!files.length) return;
      this.createImage(files[0], 2);
    },
    passportFrontChange(e) {
      var files = e.target.files || e.dataTransfer.files;
      if (!files.length) return;
      this.createImage(files[0], 3);
    },
    passportBackChange(e) {
      var files = e.target.files || e.dataTransfer.files;
      if (!files.length) return;
      this.createImage(files[0], 4);
    },
    profilePicChange(e) {
      var files = e.target.files || e.dataTransfer.files;
      if (!files.length) return;
      this.createProfileImage(files[0], 5);
    },
    CnicFrontChange(e) {
      var files = e.target.files || e.dataTransfer.files;
      if (!files.length) return;
      this.createImage(files[0], 6);
    },
    CnicBackChange(e) {
      var files = e.target.files || e.dataTransfer.files;
      if (!files.length) return;
      this.createImage(files[0], 7);
    },
    createProfileImage(file, status) {
      this.x.error = null;
      let size = file.size / 1024 / 1024;
      // -------- FILE SIZE CHECK
      if (size > 5) {
        this.$refs.profilePicUpload.value = "";
        this.x.error = "Please upload proof with size less than 5MB!";

        return false;
      }
      if (
        file.type
          .toString()
          .substr(file.type.toString().lastIndexOf(".") + 1) !== "image/png" &&
        file.type
          .toString()
          .substr(file.type.toString().lastIndexOf(".") + 1) !== "image/jpg" &&
        file.type
          .toString()
          .substr(file.type.toString().lastIndexOf(".") + 1) !== "image/jpeg"
      ) {
        this.$refs.profilePicUpload.value = "";

        this.x.error = "Please upload proof of file type png , jpg or jpeg !";

        return false;
      } else {
        var image = new Image();
        var reader = new FileReader();
        var vm = this;

        reader.onload = (e) => {
          vm.image = e.target.result;
        };
        reader.readAsDataURL(file);
        this.upload(file, status);
      }
    },
    createImage(file, status) {
      this.x.error = null;
      let size = file.size / 1024 / 1024;
      // -------- FILE SIZE CHECK
      if (size > 5) {
        this.$refs.fileUpload.value = "";
        this.x.error = "Please upload proof with size less than 5MB!";
        setTimeout(() => {
          this.x.error = null;
        }, 4000);

        return false;
      }
      if (
        file.type
          .toString()
          .substr(file.type.toString().lastIndexOf(".") + 1) !== "image/png" &&
        file.type
          .toString()
          .substr(file.type.toString().lastIndexOf(".") + 1) !== "image/jpg" &&
        file.type
          .toString()
          .substr(file.type.toString().lastIndexOf(".") + 1) !== "image/jpeg" &&
        file.type
          .toString()
          .substr(file.type.toString().lastIndexOf(".") + 1) !==
          "application/pdf"
      ) {
        this.$refs.fileUpload.value = "";

        this.x.error =
          "Please upload proof of file type png , jpg ,jpeg or pdf!";

        return false;
      } else {
        var image = new Image();
        var reader = new FileReader();
        var vm = this;

        reader.onload = (e) => {
          vm.image = e.target.result;
        };
        reader.readAsDataURL(file);
        this.upload(file, status);
      }
    },
    upload(file, statusChange) {
      this.loading = true;
      const formData = new FormData();
      formData.append("image", file);
      this.axios({
        url: `${this.constants.apiUrl}/dmsAdmin/uploadImage`,
        method: "POST",
        data: formData,
        headers: {
          Accept: "application/json",
          "Content-Type": "multipart/form-data",
        },
      }).then(
        (response) => {
          const { statusCode } = response.data;
          if (statusCode === 200) {
            this.loading = false;
            if (statusChange == 1) {
              this.tradeLicense = response.data.data.original;
            } else if (statusChange == 2) {
              this.vatCertificate = response.data.data.original;
            } else if (statusChange == 3) {
              this.passportFront = response.data.data.original;
            } else if (statusChange == 4) {
              this.passportBack = response.data.data.original;
            } else if (statusChange == 5) {
              this.profilePicURL = response.data.data.original;
            } else if (statusChange == 6) {
              this.CNICFront = response.data.data.original;
            } else if (statusChange == 7) {
              this.CNICBack = response.data.data.original;
            }
          } else {
            this.error = "Failed to upload image";
          }
          this.loading = false;
        },
        (error) => {
          this.error = "Failed to upload image";
          this.loading = false;
        }
      );
    },
    getJobTypeValue(value) {
      switch (value) {
        case "1":
          return "Land";
        case "2":
          return "Air";
        case "3":
          return "Sea";
        default:
          return "Land";
      }
    },
    close() {
      this.$router.push(`/transporter`);
    },
    checkValue() {
      this.communicateDialog.search = "";
    },
    closeRegistration() {
      this.x.registerSuccess = false;
      this.$router.go(-1);
    },
    updateTags() {
      this.$nextTick(() => {
        this.communicateDialog.select.push(
          ...this.communicateDialog.search.split(",")
        );
        this.$nextTick(() => {
          this.communicateDialog.search = "";
        });
      });
    },
    getTransporter() {
      if (!navigator.onLine) {
        this.loading = false;
        this.x.error = "Please check your internet connection";

        return;
      }

      this.loading = true;
      let url = transporter.transporterDetails;
      delete this.axios.defaults.headers.common["token"];
      let token = localStorage.getItem(StorageKeys.SessionKey);
      let config = {
        headers: {
          authorization: `bearer ${token}`,
        },
      };
      let body = {
        transporterId: this.$route.params.id,
      };
      this.axios.post(this.constants.apiUrl + url, body, config).then(
        (response) => {
          this.loading = false;
          this.type = response.data.data.transporterType;
          this.ContactPersonName = response.data.data.ContactPersonName;
          this.STRNNumber = response.data.data.STRNNumber;
          this.NTNNumber = response.data.data.NTNNumber;
          this.transporterId = response.data.data._id;
          this.countryCode = response.data.data.countryCode;
          this.profilePicURL = response.data.data.profilePicURL;
          this.phoneNo = response.data.data.phoneNo;
          this.companyName = response.data.data.companyName;
          this.username = response.data.data.transporterName;
          this.emailId = response.data.data.email;
          this.countrySortName = response.data.data.workingCountry;
          this.tradeLicenseNumber = response.data.data.tradeLicenseNumber;
          this.bankHolderName = response.data.data.bankHolderName;
          this.bankName = response.data.data.bankName;
          if (response.data.data.vatNumber) {
            this.vatNumber = response.data.data.vatNumber;
          }

          this.passport = response.data.data.passportNumber;

          this.tradeLicense = response.data.data.tradeLicense;
          this.vatCertificate = response.data.data.vatCertificate;
          this.passportFront = response.data.data.passportFront;
          this.passportBack = response.data.data.passportBack;
          this.CNICFront = response.data.data.cnicFrontImage;
          this.CNICBack = response.data.data.cnicBackImage;
          this.UserType = response.data.data.jobType;
          this.$eventBus.$emit("transporter-type", this.UserType);
          if (!response.data.data.jobType) {
            this.UserType = "1";
          }
          this.model = response.data.data.secondaryEmail;
        },
        (error) => {
          this.x.error = "Failed to Fetch User Details";
        }
      );
    },

    editTransporter() {
      if (this.$refs.form.validate()) {
        if (this.processing) {
          return;
        } else {
          this.x.error = null;
          if (this.password && !this.password.match(/^[a-zA-Z0-9]{4,}$/)) {
            this.x.error =
              "Please provide password length of minimum 4 characters";
            this.x.loading = false;
            return false;
          }
          if (
            !this.tradeLicense &&
            this.UserType == 1 &&
            this.countrySortName == "PAK"
          ) {
            this.x.error =
              "Please provide NTN certificate image before moving ahead";
            this.x.loading = false;
            return false;
          }
          if (
            !this.tradeLicenseNumber &&
            this.UserType == 1 &&
            this.countrySortName != "PAK"
          ) {
            this.x.error =
              "Please provide trade license number before moving ahead";
            this.x.loading = false;
            return false;
          }
          if (
            !this.tradeLicense &&
            this.UserType == 1 &&
            this.countrySortName != "PAK"
          ) {
            this.x.error =
              "Please provide trade license image before moving ahead";
            this.x.loading = false;
            return false;
          }
          // if (
          //   !this.vatNumber &&
          //   this.UserType == 1 &&
          //   this.countrySortName != "PAK" &&
          //   this.type == "Transporter"
          // ) {
          //   this.x.error = "Please provide VAT number before moving ahead";
          //   this.x.loading = false;
          //   return false;
          // }
          if (
            !this.vatCertificate &&
            this.UserType == 1 &&
            this.countrySortName != "PAK" &&
            this.type == "Transporter"
          ) {
            this.x.error =
              "Please provide VAT certificate image before moving ahead";
            this.x.loading = false;
            return false;
          }

          if (
            !this.passport &&
            this.UserType == 1 &&
            this.countrySortName != "PAK"
          ) {
            this.x.error = "Please provide passport number before moving ahead";
            this.x.loading = false;
            return false;
          }
          if (
            !this.passportFront &&
            this.UserType == 1 &&
            this.countrySortName != "PAK"
          ) {
            this.x.error =
              "Please provide passport front image before moving ahead";
            this.x.loading = false;
            return false;
          }
          if (
            !this.passportBack &&
            this.UserType == 1 &&
            this.countrySortName != "PAK"
          ) {
            this.x.error =
              "Please provide passport back image before moving ahead";
            this.x.loading = false;
            return false;
          }
          if (
            !this.CNICFront &&
            this.UserType == 1 &&
            this.countrySortName == "PAK"
          ) {
            this.x.error =
              "Please provide CNIC front image before moving ahead";
            this.x.loading = false;
            return false;
          }
          if (
            !this.CNICBack &&
            this.UserType == 1 &&
            this.countrySortName == "PAK"
          ) {
            this.x.error = "Please provide CNIC back image before moving ahead";
            this.x.loading = false;
            return false;
          }
          if (this.model.length) {
            let count_valid = 0;
            // let regex = '/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/';
            var re =
              /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            for (let k = 0; k < this.model.length; k++) {
              this.model[k] = this.model[k].trim();

              if (re.test(this.model[k])) {
                count_valid = count_valid + 1;
              }
            }
            if (count_valid !== this.model.length) {
              this.x.error =
                "Please make sure all the emails are properly formed";

              this.loading = false;
              return false;
            }
          }

          this.loading = true;
          let url = transporter.editTransporter;
          delete this.axios.defaults.headers.common["token"];
          let token = localStorage.getItem(StorageKeys.SessionKey);
          let config = {
            headers: {
              authorization: `bearer ${token}`,
            },
          };
          let body = {
            transporterId: this.transporterId.toString(),
            userName: this.username.trim(),
            companyName: this.companyName.trim(),
            comment: this.comment,
            emailId: this.emailId,
            jobType: this.UserType,
            countryCode: this.countryCode.dialing_code || this.countryCode,
            phoneNo: this.phoneNo,
            workingCountry: this.countrySortName,
            secondaryEmail: this.model,
            transporterType: this.type,
          };
          if (this.countrySortName == "PAK") {
            if (this.CNICFront) {
              body.cnicFrontImage = this.CNICFront;
            }
            if (this.CNICBack) {
              body.cnicBackImage = this.CNICBack;
            }
            if (this.STRNNumber) {
              body.STRNNumber = this.STRNNumber;
            }
            body.NTNNumber = this.NTNNumber;
            body.ContactPersonName = this.ContactPersonName;
          }

          if (this.UserType == 1) {
            body.tradeLicense = this.tradeLicense;
            if (this.countrySortName != "PAK") {
              body.tradeLicenseNumber = this.tradeLicenseNumber;
              body.passportNumber = this.passport;
              if (this.vatNumber.trim()) {
                body.vatNumber = this.vatNumber;
              }
            }
            if (this.vatCertificate) {
              body.vatCertificate = this.vatCertificate;
            }
            if (this.bankName) {
              body.bankName = this.bankName;
            }
            if (this.bankHolderName) {
              if (this.bankHolderName.trim()) {
                body.bankHolderName = this.bankHolderName.trim();
              }
            }
            if (this.passportFront) {
              body.passportFront = this.passportFront;
            }
            if (this.passportBack) {
              body.passportBack = this.passportBack;
            }
          }
          if (this.UserType == 2 || this.UserType == 3) {
            if (this.tradeLicense) {
              body.tradeLicense = this.tradeLicense;
            }
            if (this.countrySortName != "PAK") {
              if (this.tradeLicenseNumber) {
                body.tradeLicenseNumber = this.tradeLicenseNumber;
              }
              if (this.vatNumber.trim()) {
                body.vatNumber = this.vatNumber;
              }
              if (this.passportNumber) {
                body.passportNumber = this.passport;
              }
            }
            if (this.vatCertificate) {
              body.vatCertificate = this.vatCertificate;
            }
            if (this.passportFront) {
              body.passportFront = this.passportFront;
            }
            if (this.passportBack) {
              body.passportBack = this.passportBack;
            }
          }

          if (this.password) {
            body.password = this.password;
          }
          if (this.profilePicURL) {
            body.profilePicURL = this.profilePicURL;
          }

          this.axios.post(this.constants.apiUrl + url, body, config).then(
            (response) => {
              if (response.status === 200) {
                this.items = response.data.data;
                this.x.registerSuccess = true;

                this.loading = false;
              } else {
                this.x.error = "Failed to add transporter";
              }
            },
            (error) => {
              this.loading = false;
              this.processing = false;
              this.x.error = error.response.data.message;
            }
          );
        }
      } else {
        this.x.error = "Please fill all required fields in above form! ";
      }
    },
  },
};
</script>

<style scoped lang="scss">
.menu,
.menu__content {
  background: white !important;
}

.thumb-img {
  border: 2px solid darkorange;
  border-radius: 50%;
  height: 60px;
  width: 60px;
  margin-left: 5px !important;
}
.menuable__content__active {
  min-width: 290px !important;
}
</style>
